import { inject, Injectable, signal } from '@angular/core';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { LoaderService, Plan, TreatmentSettings, TreatmentStatusType } from '../core';
import { TreatmentProgress } from '../models';
import { PlanService } from './plan.service';
import { TreatmentService } from './treatment.service';
import { UserService } from './user.service';


/**
 * This service handles plan settings page data.
 */
@Injectable({ providedIn: 'root' })
export class PlanSettingsService {

	private readonly usrSrv = inject(UserService);
	private readonly planSrv = inject(PlanService);
	private readonly loaderSrv = inject(LoaderService);
	private readonly treatSrv = inject(TreatmentService);

	plan = signal<Plan | null>(null);

	progress = signal<TreatmentProgress | null>(null);
	consolidationDays = signal<number | undefined>(undefined);
	isConsolidationValid = signal<boolean>(false);
	threshold = signal<number>(0);

	isReadonly = signal<boolean>(true);


	/**
	 * Init plan settings page data.
	 */
	init(planId: string): Observable<boolean> {
		this.loaderSrv.show();
		return this.planSrv.getPlan(planId).pipe(
			tap(plan => this.plan.set(plan)),
			switchMap(plan => forkJoin([
				this.treatSrv.getTreatmentPlan(plan.id, plan.userGuid),
				this.usrSrv.isCustomerCareOtherUser(plan.userGuid)
			])),
			map(([settings, isReadonly]) => {
				if (!settings) return false;
				this.progress.set(this.treatSrv.treatmentProgressMapper(settings));
				this.isReadonly.set(isReadonly || [TreatmentStatusType.REVOKED].includes(settings.status));
				return true;
			}),
			finalize(() => this.loaderSrv.hide())
		);
	}

	/**
	 * Update plan settings.
	 */
	update(): Observable<void> {
		this.loaderSrv.show();
		const settings: TreatmentSettings = {
			consolidationDays: this.consolidationDays()!,
			daysBeforeAlert: this.threshold()
		};
		return this.treatSrv.updateSettings(this.plan()!.id, settings).pipe(
			catchError(err => {
				this.loaderSrv.hide();
				return throwError(() => err);
			})
		);
	}

	/**
	 * Reset plan settings page data.
	 */
	reset(): void {
		this.plan.set(null);
		this.progress.set(null);
		this.consolidationDays.set(undefined);
		this.isConsolidationValid.set(false);
	}
}